import { defineStore } from 'pinia';
import { stripFrameworkPrefix } from '../lib/urlUtils';
import { computed, ref } from 'vue';
import { getDebugInfo, getProjectStatus } from '../services/api';
import { WebSocketService } from '../services/websocket';
import type {
  FailedStep,
  FrameworkStatus,
  JobCosts,
  Output,
  ProjectStatus,
  ProjectStatusResponse,
  StreamMessage,
} from '../types';

export type FileStatus = 'pending' | 'started' | 'complete';

export const useProjectStore = defineStore('project', () => {
  // Core state
  const projectId = ref<string>('');
  const status = ref<ProjectStatus>('pending');
  const title = ref<string>('');
  const author = ref<string>('');
  const manuscript = ref<string | undefined>(undefined);
  const costs = ref<JobCosts | undefined>(undefined);
  const error = ref<string | null>(null);
  const stepErrors = ref<FailedStep[]>([]);
  const isPublic = ref<boolean>(false);
  const isOwner = ref<boolean>(false);

  // Toast notification for retries
  const retryToast = ref<{ message: string; visible: boolean }>({ message: '', visible: false });
  let retryToastTimeout: ReturnType<typeof setTimeout> | null = null;

  // Framework data - single source of truth for all framework-related state
  const frameworks = ref<FrameworkStatus[]>([]);

  // WebSocket service
  let wsService: WebSocketService | null = null;
  let statusTimestamp: number | undefined = undefined;

  // Status polling for pending state
  let statusPollInterval: ReturnType<typeof setInterval> | null = null;
  let statusPollErrorCount = 0;
  const maxStatusPollErrors = 5;

  // Computed properties
  const isPending = computed(() => status.value === 'pending');
  const isProcessing = computed(() => status.value === 'processing' || status.value === 'loading');
  const isComplete = computed(() => status.value === 'complete' || status.value === 'finalizing');

  // Get all outputs for the project
  const outputData = computed(() => {
    const outputs: Record<string, Output> = {};
    const keys: Record<string, string> = {};

    frameworks.value.forEach(framework => {
      framework.outputs.forEach(output => {
        const key = `${framework.frameworkId}:${output.name}`;
        outputs[key] = output;
        outputs[output.name] = output;
        keys[output.name] = key;
      });
      framework.intermediateOutputs.forEach(output => {
        const key = `${framework.frameworkId}:${output.name}`;
        outputs[key] = output;
        outputs[output.name] = output;
        keys[output.name] = key;
      });
    });

    return { outputs, keys };
  });

  const outputKeys = computed(() => outputData.value.keys);
  const outputs = computed(() => outputData.value.outputs);

  // Outputs delivered short because a generation failed part way. Derived from output status so
  // it reads the same on a fresh page load (from results.json) as it does over the websocket.
  const incompleteOutputs = computed(() =>
    frameworks.value.flatMap(framework =>
      framework.outputs
        .filter(o => o.status === 'incomplete')
        .map(o => ({
          name: o.name,
          frameworkName: framework.name,
          // "Novel Edit chapter notes" - the framework alone is ambiguous when a project has
          // several frameworks each with their own chapter notes.
          label: `${framework.name} ${stripFrameworkPrefix(o.name, framework.frameworkId.replace(/-/g, '_')).replace(/_/g, ' ')}`,
        }))
    )
  );

  // Get outputs for a specific framework
  const frameworkOutputs = computed(() => (frameworkId: string) => {
    const framework = frameworks.value.find(f => f.frameworkId === frameworkId);
    if (!framework) return [];

    return framework.outputs.map(output => ({
      name: output.name,
      status: output.status,
      content: output.content || '',
    }));
  });

  // Initialize project and load data
  async function load(projectIdParam: string) {
    projectId.value = projectIdParam;

    try {
      // Load initial status
      const data = await getProjectStatus(projectId.value);

      // Store the timestamp from status response
      statusTimestamp = (data as any).timestamp;

      // Process the response
      handleStatus(data);

      // Handle different status states
      await handleStatusChange(data.status);
    } catch (err: any) {
      console.error('Failed to load project:', err);

      if (err.message === 'PROJECT_NOT_FOUND') {
        status.value = 'not_found';
        error.value = 'Project not found. Check your link.';
      } else if (err.message === 'AUTH_REQUIRED') {
        status.value = 'error';
        error.value = 'You need to sign in to view this edit.';
      } else if (err.message === 'FORBIDDEN') {
        status.value = 'error';
        error.value = 'No access. This edit belongs to someone else.';
      } else if (err.message?.includes('fetch') || err.name === 'NetworkError') {
        status.value = 'error';
        error.value = "Can't reach the server. Check your connection and try again.";
      } else if (err.message?.includes('JSON')) {
        status.value = 'error';
        error.value = 'Got a bad response. Refresh the page or contact support.';
      } else {
        status.value = 'error';
        error.value = 'Something broke. Refresh or contact support if it keeps happening.';
      }
    }
  }

  // Handle status response (unified for both complete and processing)
  function handleStatus(data: ProjectStatusResponse) {
    // Set basic info
    if (data.title) title.value = data.title;
    if (data.author) author.value = data.author;
    if ('isPublic' in data) isPublic.value = data.isPublic || false;
    if ('isOwner' in data) isOwner.value = data.isOwner || false;

    // Store failed steps
    if (data.failedSteps) {
      stepErrors.value = data.failedSteps;
    }

    // Initialize frameworks with defaults for missing fields
    frameworks.value = data.frameworks.map(framework => {
      const currentSteps = framework.currentSteps || [];

      // Initialize startedSteps from currentSteps
      // This ensures we track which steps are running when page loads mid-processing
      const startedSteps = new Set<number>(currentSteps.map(s => s.step));

      return {
        ...framework,
        currentSteps,
        startedSteps,
        status: framework.status || 'pending',
        intermediateOutputs: (framework.intermediateOutputs || []).map(
          toOutput.bind(null, data.status === 'complete' || data.status === 'finalizing')
        ),
        // Ensure outputs have proper defaults
        outputs: (framework.outputs || (framework as any).files).map(
          toOutput.bind(null, data.status === 'complete' || data.status === 'finalizing')
        ),
        completedSteps: framework.completedSteps ?? 0,
        totalSteps: framework.totalSteps ?? 0,
      };
    });
  }

  function toOutput(isProjectComplete: boolean, output: any): Output {
    return {
      name: output.name,
      status: output.status || (isProjectComplete ? 'complete' : 'pending'),
      content: output.content || '',
    };
  }

  // Handle status changes and determine next action
  async function handleStatusChange(newStatus: ProjectStatus) {
    status.value = newStatus;

    if (newStatus === 'processing' || newStatus === 'finalizing') {
      // Processing has started, connect WebSocket
      await connectWebSocket();
    } else if (newStatus === 'pending') {
      // Processing hasn't started yet, poll until it does
      startStatusPolling();
    }
  }

  // Connect to WebSocket for real-time updates
  async function connectWebSocket(): Promise<boolean> {
    if (!projectId.value) return false;

    // Stop polling if it's running
    stopStatusPolling();

    wsService = new WebSocketService(projectId.value);

    // Set up reconnection handler to refresh timestamp
    wsService.onReconnect(async () => {
      try {
        console.log('WebSocket reconnected, fetching fresh status for sync');
        const data = await getProjectStatus(projectId.value);
        statusTimestamp = (data as any).timestamp;

        // Send sync with fresh timestamp
        if (statusTimestamp !== undefined) {
          console.log('Sending sync message after reconnect with fresh timestamp:', statusTimestamp);
          wsService?.send({ type: 'sync', timestamp: statusTimestamp });
        }
      } catch (err) {
        console.error('Failed to refresh status on reconnect:', err);

        // Disconnect WebSocket if we can’t sync
        wsService?.disconnect(true);
        wsService = null;

        // Set error state with recovery option
        status.value = 'error';
        error.value = 'Lost connection. Unable to resume monitoring your edit progress.';
      }
    });

    // Set up disconnected handler (max reconnection attempts reached)
    wsService.onDisconnected(() => {
      console.log('WebSocket permanently disconnected, falling back to status polling');

      // Fall back to HTTP polling every 3 seconds
      startProcessingStatusPolling();
    });

    try {
      await wsService.connect();

      // Send sync message with timestamp if we have one
      if (statusTimestamp !== undefined) {
        console.log('Sending sync message with timestamp:', statusTimestamp);
        wsService.send({ type: 'sync', timestamp: statusTimestamp });
      } else {
        console.warn('No timestamp available for sync, sending 0');
        wsService.send({ type: 'sync', timestamp: 0 });
      }

      // Handle streaming messages
      wsService.onMessage((message: StreamMessage) => {
        handleStreamMessage(message);
      });

      return true;
    } catch (error) {
      console.error('Failed to connect WebSocket:', error);

      // Fall back to HTTP polling
      console.log('WebSocket connection failed, falling back to status polling');
      startProcessingStatusPolling();

      return false;
    }
  }

  // Start polling status endpoint until processing begins
  function startStatusPolling() {
    // Clear any existing polling (defensive programming)
    if (statusPollInterval !== null) {
      console.warn('Status polling already active, stopping before starting new interval');
      stopStatusPolling();
    }

    console.log('Starting status polling (pending state)');

    // Reset error count
    statusPollErrorCount = 0;

    // Poll every second
    statusPollInterval = setInterval(async () => {
      try {
        const data = await getProjectStatus(projectId.value);

        // Reset error count on success
        statusPollErrorCount = 0;

        // Update timestamp
        statusTimestamp = (data as any).timestamp;

        // Update frameworks and metadata
        handleStatus(data);

        // Check if status has changed from pending
        if (data.status !== 'pending') {
          console.log('Status changed from pending to', data.status);
          stopStatusPolling();
          await handleStatusChange(data.status);
        }
      } catch (error: any) {
        console.error('Status polling error:', error);
        statusPollErrorCount++;

        // Stop polling after multiple consecutive failures
        if (statusPollErrorCount >= maxStatusPollErrors) {
          console.error(`Status polling stopped after ${maxStatusPollErrors} consecutive errors`);
          stopStatusPolling();

          // Set error state with actionable message
          if (error.message === 'PROJECT_NOT_FOUND') {
            status.value = 'not_found';
            error.value = 'Project not found. Check your link.';
          } else if (error.message === 'AUTH_REQUIRED') {
            status.value = 'error';
            error.value = 'You need to sign in to view this edit.';
          } else if (error.message === 'FORBIDDEN') {
            status.value = 'error';
            error.value = 'No access. This edit belongs to someone else.';
          } else {
            status.value = 'error';
            error.value = 'Connection lost. Refresh to try again.';
          }
        }
      }
    }, 1000);
  }

  // Start polling during processing (fallback when WebSocket fails)
  function startProcessingStatusPolling() {
    // Clear any existing polling
    stopStatusPolling();

    console.log('Starting processing status polling (WebSocket fallback)');

    // Reset error count
    statusPollErrorCount = 0;

    // Poll every 3 seconds (less aggressive than pending polling)
    statusPollInterval = setInterval(async () => {
      try {
        const data = await getProjectStatus(projectId.value);

        // Reset error count on success
        statusPollErrorCount = 0;

        // Update timestamp
        statusTimestamp = (data as any).timestamp;

        // Update frameworks and metadata
        handleStatus(data);

        // Stop polling if complete
        if (data.status === 'complete' || data.status === 'finalizing') {
          console.log('Processing complete, stopping polling');
          stopStatusPolling();

          // Reload final data
          await load(projectId.value);
        }
      } catch (error: any) {
        console.error('Processing status polling error:', error);
        statusPollErrorCount++;

        // Stop after max errors
        if (statusPollErrorCount >= maxStatusPollErrors) {
          console.error('Processing status polling stopped after max errors');
          stopStatusPolling();

          status.value = 'error';
          error.value = 'Lost connection. Refresh the page to continue.';
        }
      }
    }, 3000);
  }

  // Stop polling status endpoint
  function stopStatusPolling() {
    if (statusPollInterval) {
      clearInterval(statusPollInterval);
      statusPollInterval = null;
      console.log('Status polling stopped');
    }
  }

  // Show a transient toast
  function showToast(message: string) {
    // Clear any existing timeout
    if (retryToastTimeout) {
      clearTimeout(retryToastTimeout);
    }

    retryToast.value = { message, visible: true };

    // Auto-hide after 5 seconds
    retryToastTimeout = setTimeout(() => {
      retryToast.value.visible = false;
    }, 5000);
  }

  // Handle WebSocket stream messages
  function handleStreamMessage(message: StreamMessage) {
    switch (message.type) {
      case 'steps_start': {
        // Handle multiple steps starting
        message.steps.forEach(step => {
          const framework = frameworks.value.find(f => f.frameworkId === step.frameworkId);
          if (!framework) return;

          // Initialize startedSteps set if it doesn’t exist
          if (!framework.startedSteps) {
            framework.startedSteps = new Set<number>();
          }

          // Mark step as started
          framework.startedSteps.add(step.step);

          // Check if step is already in currentSteps
          const stepExists = framework.currentSteps.some(s => s.step === step.step);
          if (stepExists) return;

          // Add to framework's current steps
          const currentStep: { name: string; description: string; step: number; output?: string } = {
            name: step.name,
            description: step.description,
            step: step.step,
            ...(step.output && { output: step.output }),
          };
          framework.currentSteps.push(currentStep);
        });

        // Trigger reactivity
        frameworks.value = [...frameworks.value];
        break;
      }

      case 'step_progress': {
        // Update output content if output is specified
        if (message.output && message.content) {
          const framework = frameworks.value.find(f => f.frameworkId === message.frameworkId);
          if (!framework) return;

          const output = framework.outputs.find(o => o.name === message.output);
          if (output) {
            // Append content and mark as started (since we now have content)
            output.content = (output.content || '') + message.content;
            if (output.status === 'pending') {
              output.status = 'started';
            }
          } else {
            // Create new output entry with started status (we have content)
            framework.outputs.push({
              name: message.output,
              status: 'started',
              content: message.content,
            });
          }

          // Trigger reactivity
          frameworks.value = [...frameworks.value];
        }
        break;
      }

      case 'step_retry': {
        // The server rebases an output onto the content it has confirmed so far, rather than
        // throwing away everything a dropped stream had already produced.
        const framework = frameworks.value.find(f => f.frameworkId === message.frameworkId);
        if (!framework) return;

        const output = framework.outputs.find(o => o.name === message.output);
        if (output) {
          output.content = message.content;
          output.status = 'started';

          if (message.notify) {
            showToast(
              message.resumedFromChapter
                ? `Still writing ${framework.name}. Picking up from Chapter ${message.resumedFromChapter}.`
                : `Hit a snag on ${framework.name}. Starting that section again.`
            );
          }

          // Trigger reactivity
          frameworks.value = [...frameworks.value];
        }
        break;
      }

      case 'step_incomplete': {
        // Delivered, but short. A deferred run will try to finish it.
        const framework = frameworks.value.find(f => f.frameworkId === message.frameworkId);
        if (!framework) return;

        const output = framework.outputs.find(o => o.name === message.output);
        if (output) {
          output.status = 'incomplete';
          console.warn(
            `${message.output} delivered incomplete; missing chapters: ${message.missingChapters.join(', ') || 'unknown'}`
          );
          frameworks.value = [...frameworks.value];
        }
        break;
      }

      case 'step_complete': {
        // Update framework
        const framework = frameworks.value.find(f => f.frameworkId === message.frameworkId);
        if (framework) {
          // Initialize startedSteps if needed
          if (!framework.startedSteps) {
            framework.startedSteps = new Set<number>();
          }

          // Only increment if this step was previously started
          // This prevents double-counting if step_complete arrives twice (e.g., after DO restart)
          if (framework.startedSteps.has(message.step)) {
            framework.completedSteps++;
            framework.startedSteps.delete(message.step);
          }

          // Remove from current steps
          framework.currentSteps = framework.currentSteps.filter(s => s.step !== message.step);

          // Only mark output as complete if it's provided (should only be for final outputs)
          if (message.output) {
            // Check if this output already exists in the outputs array
            const output = framework.outputs.find(o => o.name === message.output);

            if (output) {
              // The completion message carries the outcome, so a later run that finishes a
              // salvaged document clears the "still finishing" notice on its own.
              output.status = message.incomplete ? 'incomplete' : 'complete';
            }
          }

          // Trigger reactivity
          frameworks.value = [...frameworks.value];
        }
        break;
      }

      case 'framework_complete': {
        // Check if all frameworks are complete
        // For now, we’ll rely on the all_complete message
        break;
      }

      case 'metadata': {
        // Update title and author
        if (message.title) title.value = message.title;
        if (message.author) author.value = message.author;
        break;
      }

      case 'all_complete': {
        // Disconnect WebSocket first
        if (wsService) {
          wsService.disconnect(true);
          wsService = null;
        }

        // Reload to get final data (async fire-and-forget)
        load(projectId.value)
          .then(() => {
            status.value = 'complete';
          })
          .catch(error => {
            console.error('Failed to load final project data:', error);

            // Set status to complete anyway (we have WebSocket data)
            status.value = 'complete';

            // Show warning to user
            error.value = 'Your edit is complete, but some final data couldn’t load. Try refreshing.';
          });
        break;
      }

      case 'error': {
        console.error(`Error in ${message.frameworkId} step ${message.step}:`, message.error);

        // Find the framework and mark it as failed
        const framework = frameworks.value.find(f => f.frameworkId === message.frameworkId);
        if (framework) {
          // Add to step errors for display
          stepErrors.value.push({
            frameworkId: message.frameworkId,
            step: message.step,
            name: framework.name,
            error: message.error,
          });

          // Show error toast notification
          showToast(`${framework.name} couldn’t finish. We’ve been notified.`);

          // Trigger reactivity
          frameworks.value = [...frameworks.value];
        }
        break;
      }
    }
  }

  // Load debug information
  async function loadDebugInfo() {
    if (!projectId.value) return;

    try {
      const debugInfo = await getDebugInfo(projectId.value);

      // Merge debug info
      if (debugInfo.manuscript && !manuscript.value) {
        manuscript.value = debugInfo.manuscript;
      }
      if (debugInfo.costs && !costs.value) {
        costs.value = debugInfo.costs;
      }

      // Add intermediate outputs to frameworks
      if (debugInfo.frameworkIntermediateOutputs) {
        for (const framework of frameworks.value) {
          const intermediateOutputs = debugInfo.frameworkIntermediateOutputs[framework.frameworkId];
          if (intermediateOutputs) {
            if (Array.isArray(intermediateOutputs)) {
              framework.intermediateOutputs = intermediateOutputs.map(toOutput.bind(null, true));
            } else {
              framework.intermediateOutputs = Object.entries(intermediateOutputs).map(([name, content]) =>
                toOutput(true, { name, content })
              );
            }
          }
        }
        // Trigger reactivity
        frameworks.value = [...frameworks.value];
      }

      // Store step errors
      if (debugInfo.stepErrors) {
        stepErrors.value = debugInfo.stepErrors;
      }

      return true;
    } catch (err: any) {
      console.error('Failed to load debug info:', err);

      // Set error message for debug UI
      error.value = 'Unable to load debug information: ' + (err.message || 'Unknown error. Try again.');

      return false;
    }
  }

  // Cleanup
  function cleanup() {
    // Stop status polling
    stopStatusPolling();

    // Disconnect WebSocket
    if (wsService) {
      wsService.disconnect(true);
      wsService = null;
    }

    // Clear retry toast timeout
    if (retryToastTimeout) {
      clearTimeout(retryToastTimeout);
      retryToastTimeout = null;
    }

    // Reset all state
    projectId.value = '';
    status.value = 'pending';
    title.value = '';
    author.value = '';
    manuscript.value = undefined;
    costs.value = undefined;
    error.value = null;
    frameworks.value = [];
    retryToast.value = { message: '', visible: false };
    isOwner.value = false;
  }

  return {
    // State
    projectId,
    status,
    title,
    author,
    manuscript,
    costs,
    error,
    frameworks,
    retryToast,
    incompleteOutputs,
    stepErrors,
    isPublic,
    isOwner,

    // Computed
    isPending,
    isProcessing,
    isComplete,
    frameworkOutputs,
    outputKeys,
    outputs,

    // Actions
    load,
    loadDebugInfo,
    cleanup,
  };
});
